"use client";

import { Image } from "@repo/components/atoms/image";
import type { PropsWithChildren, Usable } from "react";
import React, { Children, isValidElement, useState } from "react";
import type { ResolvedIntlConfig } from "react-intl";
import { Button } from "@repo/components/atoms/shadcn/button";
import {
  Carousel,
  CarouselContent,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@repo/components/atoms/shadcn/carousel";
import { useGetIntl } from "@repo/i18n/intl/client";
import { cn } from "@repo/lib";

type CarouselOptions = React.ComponentProps<typeof Carousel>["opts"];

interface MediaChildProps {
  "data-thumbnail-enabled": boolean;
  "data-thumbnail-id": string;
  "data-thumbnail-src": string;
  "data-thumbnail-blur-data-url": string;
}

export function MediaViewer({
  children,
  className,
  dictionaryPromise,
  lang,
  title,
}: PropsWithChildren<{
  className?: string;
  dictionaryPromise: Usable<ResolvedIntlConfig["messages"]>;
  lang: Navigator["language"];
  title: string;
}>) {
  const opts = {
    align: "start",
    dragFree: true,
    loop: true,
  } satisfies CarouselOptions;

  const [api, setApi] = useState<CarouselApi>();

  const intl = useGetIntl({
    dictionaryPromise,
    lang,
  });

  const thumbnailClassName =
    "pointer-events-auto w-20 relative my-auto aspect-square h-auto overflow-hidden rounded-full bg-background/50 shadow-md border border-background/50 cursor-crosshair *:scale-200 hover:*:scale-180 focus:*:scale-180 *:transition-transform *:duration-1000 *:ease-in-out";

  const arrayChildren = Children.toArray(children);

  const slides = arrayChildren.filter((child) =>
    isValidElement<MediaChildProps>(child),
  );

  const thumbnails = slides.filter(
    (slide) => slide.props["data-thumbnail-enabled"] === true,
  );

  return (
    <>
      <Carousel
        className={cn("landscape:h-dvh [&_>_div]:h-full grid", className)}
        opts={opts}
        setApi={setApi}
      >
        <CarouselContent className="ms-0 h-full cursor-grab">
          {slides}
        </CarouselContent>
        <CarouselPrevious
          className="lg:hidden start-4 self-center"
          title={intl.formatMessage(
            {
              defaultMessage: "Previous {title} image",
              description: "Button to show previous image in carousel",
              id: "LL1Pcc",
            },
            { title },
          )}
        />
        <CarouselNext
          className="lg:hidden end-4 self-center"
          title={intl.formatMessage(
            {
              defaultMessage: "Next {title} image",
              description: "Button to show next image in carousel",
              id: "WlfIG6",
            },
            { title },
          )}
        />
      </Carousel>
      <nav className="pointer-events-none absolute px-8 top-0 z-30 hidden h-full grid-flow-row content-center gap-4 lg:grid backdrop-blur-xs">
        {thumbnails.map((thumbnail, index) => (
          <Button
            aria-label={intl.formatMessage(
              {
                defaultMessage: "Show {title} image",
                description: "Button to show image in carousel",
                id: "dOx5bu",
              },
              { index: index + 1, title },
            )}
            className={thumbnailClassName}
            key={thumbnail.props["data-thumbnail-id"]}
            onClick={() => {
              api?.scrollTo(index);
            }}
            title={intl.formatMessage(
              {
                defaultMessage: "Show {title} image",
                description: "Button to show image in carousel",
                id: "dOx5bu",
              },
              { index: index + 1, title },
            )}
          >
            <Image
              alt={intl.formatMessage(
                {
                  defaultMessage: "Show {title} image",
                  description: "Button to show image in carousel",
                  id: "dOx5bu",
                },
                { index: index + 1, title },
              )}
              blurDataURL={thumbnail.props["data-thumbnail-blur-data-url"]}
              fill
              sizes="5vw"
              src={thumbnail.props["data-thumbnail-src"]}
              style={{
                objectFit: "cover",
              }}
            />
          </Button>
        ))}
      </nav>
    </>
  );
}
